import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { useApp } from '../context/AppContext';
import { getDashboardStats, getRevenueAnalytics, getCrmActivity, getAppStats } from '../services/api';
import './Dashboard.css';

const ACTION_COLOR = a => {
  if (!a) return '#8a9a97';
  if (a.includes('ACTIVATED') || a.includes('COMPLETED') || a.includes('PURCHASED') || a.includes('CREATE')) return '#A7F432';
  if (a.includes('REGISTERED') || a.includes('CREATED') || a.includes('DOWNLOADED') || a.includes('UPDATE')) return '#4fc3f7';
  if (a.includes('PAYMENT') || a.includes('PAYOUT') || a.includes('DEAL')) return '#f5a623';
  return '#8a9a97';
};

function StatCard({ label, value, sub, subUp, unit = '', onClick }) {
  return (
    <div className="stat-card" onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default', transition:'transform 0.15s', ...(onClick ? { ':hover': { transform:'scale(1.02)' } } : {}) }}
      onMouseEnter={e => { if(onClick) e.currentTarget.style.transform='scale(1.02)'; }}
      onMouseLeave={e => { if(onClick) e.currentTarget.style.transform='scale(1)'; }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{unit}{typeof value === 'number' ? value.toLocaleString() : (value ?? '—')}</div>
      {sub && <div className={`stat-sub ${subUp ? 'stat-up' : ''}`}>{sub}</div>}
      {onClick && <div style={{ fontSize:10, color:'var(--accent)', marginTop:4 }}>View →</div>}
    </div>
  );
}

function KpiCard({ label, value, target, unit = '' }) {
  const pct = target ? Math.min(100, Math.round((Number(value) / target) * 100)) : null;
  const color = pct === null ? 'var(--accent)' : pct >= 100 ? '#A7F432' : pct >= 60 ? '#f5a623' : '#ff4d4d';
  const display = unit === '$' ? `$${Number(value || 0).toLocaleString()}` : `${Number(value || 0).toLocaleString()}${unit}`;
  const targetDisplay = unit === '$' ? `$${Number(target).toLocaleString()}` : `${target}${unit}`;
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ fontSize: 22 }}>{display}</div>
      {target && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-dim)', marginBottom: 4 }}>
            <span>Target: {targetDisplay}</span>
            <span style={{ color }}>{pct}%</span>
          </div>
          <div style={{ height: 3, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2, transition: 'width 0.4s' }} />
          </div>
        </div>
      )}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="tooltip-label">{label}</p>
      {payload.map((p, i) => <p key={i} style={{ color: p.color }}>{p.name}: ${Number(p.value).toLocaleString()}</p>)}
    </div>
  );
};

export default function Dashboard({ toast, onChange }) {
  const { token, role } = useApp();
  const [stats,    setStats]    = useState(null);
  const [appStats, setAppStats] = useState(null);
  const [revenue,  setRevenue]  = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [period,   setPeriod]   = useState('month');

  const isAdmin          = role === 'Super Admin' || role === 'Admin';
  const isFinance        = role === 'Finance';
  const isDeveloper      = role === 'Developer';
  const isSupport        = role === 'Support';
  const isProductManager = role === 'Product Manager';
  const showRevenue      = isAdmin || isFinance;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [s, r, a, ap] = await Promise.allSettled([
          getDashboardStats(token),
          getRevenueAnalytics(token, period),
          getCrmActivity(token),
          getAppStats(token),
        ]);
        if (cancelled) return;
        if (s.status === 'fulfilled') setStats(s.value.data || s.value);
        if (r.status === 'fulfilled') {
          const rd = r.value.data || r.value;
          setRevenue(Array.isArray(rd) ? rd : rd?.revenueByMonth || []);
        }
        if (a.status === 'fulfilled') {
          const ad = a.value.data || a.value;
          setActivity(Array.isArray(ad) ? ad : []);
        }
        if (ap.status === 'fulfilled') setAppStats(ap.value.data || ap.value);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [token, period]);

  const scouts        = appStats?.roles?.scout  || 0;
  const players       = appStats?.roles?.player || 0;
  const clubs         = appStats?.totalClubs    || 0;
  const totalAppUsers = appStats?.totalUsers    || 0;
  const pendingClubs  = appStats?.pendingClubs  || 0;
  const totalEvents   = appStats?.totalEvents   || 0;
  const totalContacts = stats?.totalContacts    || 0;
  const grossRevenue  = stats?.grossRevenue     || 0;
  const netRevenue    = stats?.netRevenue       || 0;
  const workflowsLive = stats?.workflowsLive   || 0;
  const trialsPending = stats?.trialsPending    || 0;
  const dealsTotal    = stats?.dealsTotal       || 0;

  const pieData = [
    { name: 'Players', value: players, color: '#A7F432' },
    { name: 'Scouts',  value: scouts,  color: '#4fc3f7' },
    { name: 'Clubs',   value: clubs,   color: '#a78bfa' },
  ].filter(d => d.value > 0);

  const revenueChartData = revenue.map(m => ({
    month: m.month, gross: Number(m.gross || 0), net: Number(m.net || 0),
  }));

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: 'var(--text-dim)', fontSize: 14 }}>
      Loading dashboard…
    </div>
  );

  return (
    <div className="dashboard">

      {/* ── ADMIN / SUPER ADMIN ── */}
      {isAdmin && (
        <>
          <div className="stats-grid" style={{ marginBottom: 24 }}>
            <StatCard label="Total app users" value={totalAppUsers} sub="Registered on app" subUp onClick={() => onChange?.("players")} />
            <StatCard label="Clubs on app"    value={clubs}         sub="Active clubs" subUp onClick={() => onChange?.("clubs")} />
            <StatCard label="Scouts on app"   value={scouts}        sub="Registered scouts" subUp onClick={() => onChange?.("scouts")} />
            <StatCard label="Players on app"  value={players}       sub="Registered players" subUp onClick={() => onChange?.("players")} />
            <StatCard label="Pending clubs"   value={pendingClubs}  sub="Awaiting approval" onClick={() => onChange?.("clubs")} />
            <StatCard label="Total events"    value={totalEvents}   sub="All events" onClick={() => onChange?.("events")} />
            <StatCard label="CRM contacts"    value={totalContacts} sub="Total leads" onClick={() => onChange?.("contacts")} />
            <StatCard label="Workflows live"  value={workflowsLive} sub="Running" subUp />
          </div>
          <div style={{ marginBottom: 6, fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Revenue (30-day targets)</div>
          <div className="stats-grid" style={{ marginBottom: 24 }}>
            <KpiCard label="Gross revenue" value={grossRevenue} target={4050}  unit="$" />
            <KpiCard label="Net revenue"   value={netRevenue}   target={2835}  unit="$" />
            <KpiCard label="Evaluations"   value={dealsTotal}   target={300} />
            <KpiCard label="Trials pending" value={trialsPending} target={50} />
          </div>
        </>
      )}

      {/* ── FINANCE ── */}
      {isFinance && (
        <>
          <div className="stats-grid" style={{ marginBottom: 24 }}>
            <StatCard label="Gross revenue"   value={grossRevenue}  sub="All time" unit="$" subUp />
            <StatCard label="Net revenue"     value={netRevenue}    sub="After fees" unit="$" subUp />
            <StatCard label="Total app users" value={totalAppUsers} sub="Registered" />
            <StatCard label="Scouts on app"   value={scouts}        sub="Earning scouts" subUp onClick={() => onChange?.("scouts")} />
          </div>
          <div style={{ marginBottom: 6, fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Revenue (30-day targets)</div>
          <div className="stats-grid" style={{ marginBottom: 24 }}>
            <KpiCard label="Gross revenue" value={grossRevenue} target={4050}  unit="$" />
            <KpiCard label="Net revenue"   value={netRevenue}   target={2835}  unit="$" />
            <KpiCard label="Evaluations"   value={dealsTotal}   target={300} />
            <KpiCard label="Trials pending" value={trialsPending} target={50} />
          </div>
        </>
      )}

      {/* ── PRODUCT MANAGER ── */}
      {isProductManager && (
        <div className="stats-grid" style={{ marginBottom: 24 }}>
          <StatCard label="Total app users" value={totalAppUsers} sub="Registered on app" subUp onClick={() => onChange?.("players")} />
          <StatCard label="Clubs on app"    value={clubs}         sub="Active clubs" subUp onClick={() => onChange?.("clubs")} />
          <StatCard label="Scouts on app"   value={scouts}        sub="Registered scouts" subUp onClick={() => onChange?.("scouts")} />
          <StatCard label="Players on app"  value={players}       sub="Registered players" subUp onClick={() => onChange?.("players")} />
          <StatCard label="Pending clubs"   value={pendingClubs}  sub="Awaiting approval" onClick={() => onChange?.("clubs")} />
          <StatCard label="CRM contacts"    value={totalContacts} sub="Total leads" onClick={() => onChange?.("contacts")} />
          <StatCard label="Total events"    value={totalEvents}   sub="All events" onClick={() => onChange?.("events")} />
          <StatCard label="Workflows live"  value={workflowsLive} sub="Running" subUp />
        </div>
      )}

      {/* ── SUPPORT ── */}
      {isSupport && (
        <div className="stats-grid" style={{ marginBottom: 24 }}>
          <StatCard label="Total app users" value={totalAppUsers} sub="Registered" />
          <StatCard label="CRM contacts"    value={totalContacts} sub="Total leads" onClick={() => onChange?.("contacts")} />
          <StatCard label="Clubs on app"    value={clubs}         sub="Active clubs" />
          <StatCard label="Pending clubs"   value={pendingClubs}  sub="Awaiting approval" onClick={() => onChange?.("clubs")} />
        </div>
      )}

      {/* ── DEVELOPER ── */}
      {isDeveloper && (
        <div className="stats-grid" style={{ marginBottom: 24 }}>
          <StatCard label="Total app users" value={totalAppUsers} sub="Registered" />
          <StatCard label="CRM contacts"    value={totalContacts} sub="Total leads" onClick={() => onChange?.("contacts")} />
          <StatCard label="Workflows live"  value={workflowsLive} sub="Running" subUp />
          <StatCard label="Total events"    value={totalEvents}   sub="All events" onClick={() => onChange?.("events")} />
        </div>
      )}

      {/* ── Charts — Finance & Admin only ── */}
      {showRevenue && (
        <div className="two-col" style={{ marginBottom: 24 }}>
          <div className="card">
            <div className="card-header">
              <span className="card-title">Revenue by {period}</span>
              <div style={{ display:'flex', gap:4 }}>
                {['hour','day','month','year'].map(p => (
                  <button key={p} onClick={() => setPeriod(p)}
                    style={{ fontSize:10, padding:'3px 8px', borderRadius:20, border:'1px solid var(--border)', cursor:'pointer',
                      background: period===p ? 'var(--accent)' : 'var(--bg-input)',
                      color: period===p ? '#0a0a0a' : 'var(--text-dim)', fontWeight: period===p ? 600 : 400, textTransform:'capitalize' }}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            {revenueChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={revenueChartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="grossGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#A7F432" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#A7F432" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="netGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#4fc3f7" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#4fc3f7" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" tick={{ fill: '#646B6B', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#646B6B', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="gross" name="Gross" stroke="#A7F432" strokeWidth={2} fill="url(#grossGrad)" />
                  <Area type="monotone" dataKey="net"   name="Net"   stroke="#4fc3f7" strokeWidth={2} fill="url(#netGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dim)', fontSize: 13 }}>No revenue data yet</div>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">User breakdown</span>
              <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{totalAppUsers} total app users</span>
            </div>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value">
                    {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Legend iconType="circle" iconSize={8} formatter={(v, entry) => (
                    <span style={{ color: '#8a9a97', fontSize: 12 }}>{v} ({entry.payload.value})</span>
                  )} />
                  <Tooltip formatter={(v, n) => [`${v} users`, n]} contentStyle={{ background: '#131B1B', border: '1px solid #2a3a3a', borderRadius: 8, color: '#e5eee5' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dim)', fontSize: 13 }}>No user data yet</div>
            )}
          </div>
        </div>
      )}

      {/* ── User breakdown for non-finance roles ── */}
      {(isAdmin || isProductManager) && !showRevenue && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header">
            <span className="card-title">User breakdown</span>
            <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{totalAppUsers} total</span>
          </div>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value">
                  {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Legend iconType="circle" iconSize={8} formatter={(v, entry) => (
                  <span style={{ color: '#8a9a97', fontSize: 12 }}>{v} ({entry.payload.value})</span>
                )} />
                <Tooltip formatter={(v, n) => [`${v} users`, n]} contentStyle={{ background: '#131B1B', border: '1px solid #2a3a3a', borderRadius: 8, color: '#e5eee5' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dim)', fontSize: 13 }}>No user data yet</div>
          )}
        </div>
      )}

      {/* ── Activity Feed ── */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Live activity feed</span>
          <span className="live-indicator"><span className="live-dot" /> Live</span>
        </div>
        {activity.length > 0 ? (
          <div className="activity-list">
            {activity.map((item, i) => (
              <div key={item.id || i} className="activity-item">
                <div className="activity-dot" style={{ background: ACTION_COLOR(item.action) }} />
                <div className="activity-text">{item.description || item.action}</div>
                <div className="activity-time">{new Date(item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-dim)', fontSize: 13 }}>
            No activity yet — lifecycle events will appear here as users engage with the platform.
          </div>
        )}
      </div>
    </div>
  );
}
